import React from 'react';

import { DefaultOptionType } from 'antd/lib/select';

import {
  DossierBodyResponseDto,
  DossierDtoAdminFonctionnel,
  DossierResponseDto,
  FicheJudiciaireRequestDtoValeurJuridiqueEnum,
  UpdateDonneesJudiciairesRequestDtoNewValeurJuridiqueEnum,
  UpdateDonneesJudiciairesViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import AsyncComponent from 'components/AsyncComponent/AsyncComponent';
import BaseForm from 'components/BaseForm/BaseForm';
import { useRefJuridiction } from 'hooks/referentiels/useRefJuridiction';
import { ControllerApi } from 'hooks/updateDonneesJudiciaires/types';
import useSubmitUpdateDonneesJudiciaires from 'hooks/updateDonneesJudiciaires/useSubmitUpdateDonneesJudiciaires';
import useValidateUpdateDonneesJudiciaires from 'hooks/updateDonneesJudiciaires/useValidateUpdateDonneesJudiciaires';
import { valeurJuridiqueMapping } from 'utils/enumData';

import BaseInputNumber from '../BaseForm/BaseInputNumber';
import BaseSelect from '../BaseForm/Select/BaseSelect';
import BaseSelectJuridiction from '../BaseForm/Select/juridiction/BaseSelectJuridiction';

import useInitialValues from './useInitialValues';

import './UpdateDonneesJudiciaires.less';

type SetDossierAdmin = (dossier: DossierDtoAdminFonctionnel) => void;
type SetDossierJustice = (dossier: DossierResponseDto) => void;

interface UpdateDonneesJudiciairesProps {
  closeModal: () => void;
  controller: ControllerApi;
  dossierId: string;
  setDossier: SetDossierJustice | SetDossierAdmin;
  fetchDossierInProgress: boolean;
  fetchDossier: () => void;
  errorOccured: boolean;
  dossierBody: DossierBodyResponseDto | null | undefined;
}

function UpdateDonneesJudiciaires({
  closeModal,
  controller,
  dossierId,
  setDossier,
  fetchDossierInProgress,
  fetchDossier,
  errorOccured,
  dossierBody,
}: UpdateDonneesJudiciairesProps): React.ReactElement {
  const { data: actualJuridiction, isFetching: fetchJuridictionInProgress } =
    useRefJuridiction(
      dossierBody?.ficheJudiciaire?.idCorrelationJuridiction,
      'modalUpdateDonneesJudiciairesActualJuridiction',
    );
  const initialValues = useInitialValues(dossierBody, actualJuridiction);

  const { submit, inProgress } = useSubmitUpdateDonneesJudiciaires(
    controller,
    dossierId,
    setDossier,
    closeModal,
  );

  const { validateFields } = useValidateUpdateDonneesJudiciaires(
    controller,
    dossierId,
  );

  return (
    <AsyncComponent
      render={() => {
        return dossierBody ? (
          <>
            <h3>Mettre à jour les données judiciaires</h3>
            <BaseForm
              initialValues={initialValues}
              onSubmit={submit}
              onCancel={closeModal}
              isSubmitting={inProgress}
              inputs={[
                {
                  field:
                    UpdateDonneesJudiciairesViolationDtoFormFieldEnum.NEW_ID_CORRELATION_JURIDICTION,
                  required: true,
                  name: 'newJuridiction',
                  label: 'Juridiction',
                  dependencies: [
                    'newNumeroParquet',
                    'newNumeroPv',
                    'newNumeroIDJ',
                    'newValeurJuridique',
                  ],
                  render: () => <BaseSelectJuridiction allowClear={false} />,
                },
                {
                  label: 'N° de Parquet',
                  name: 'newNumeroParquet',
                  field:
                    UpdateDonneesJudiciairesViolationDtoFormFieldEnum.NEW_NUMERO_PARQUET,
                  dependencies: [
                    'newJuridiction',
                    'newNumeroPv',
                    'newNumeroIDJ',
                    'newValeurJuridique',
                  ],
                  render: () => <BaseInputNumber />,
                },
                {
                  label: 'N° de PV',
                  name: 'newNumeroPv',
                  field:
                    UpdateDonneesJudiciairesViolationDtoFormFieldEnum.NEW_NUMERO_PV,
                  dependencies: [
                    'newJuridiction',
                    'newNumeroParquet',
                    'newNumeroIDJ',
                    'newValeurJuridique',
                  ],
                },
                {
                  label: 'N° IDJ',
                  name: 'newNumeroIDJ',
                  field:
                    UpdateDonneesJudiciairesViolationDtoFormFieldEnum.NEW_NUMERO_IDJ,
                  dependencies: [
                    'newJuridiction',
                    'newNumeroParquet',
                    'newNumeroPv',
                    'newValeurJuridique',
                  ],
                },
                {
                  label: 'Valeur juridique',
                  name: 'newValeurJuridique',
                  field:
                    UpdateDonneesJudiciairesViolationDtoFormFieldEnum.NEW_VALEUR_JURIDIQUE,
                  dependencies: [
                    'newJuridiction',
                    'newNumeroParquet',
                    'newNumeroPv',
                    'newNumeroIDJ',
                  ],
                  render: () => {
                    const options: DefaultOptionType[] = Object.keys(
                      FicheJudiciaireRequestDtoValeurJuridiqueEnum,
                    ).map(key => ({
                      label:
                        valeurJuridiqueMapping[
                          key as UpdateDonneesJudiciairesRequestDtoNewValeurJuridiqueEnum
                        ],
                      value: key,
                    }));

                    return (
                      <BaseSelect
                        options={options}
                        getOptionValue={option => option.value as string}
                        getOptionLabel={option => option.label}
                      />
                    );
                  },
                },
              ]}
              validateField={validateFields}
              renderButtonAction={(Submit, Cancel) => {
                return (
                  <div className="UpdateDonneesJudiciairesActions">
                    <div className="UpdateAction">{Cancel}</div>
                    <div className="UpdateAction">{Submit}</div>
                  </div>
                );
              }}
            />
          </>
        ) : null;
      }}
      inProgress={fetchDossierInProgress || fetchJuridictionInProgress}
      errorOccured={errorOccured}
      reload={fetchDossier}
      errorMessage="Impossible de charger les données du dossier, veuillez réessayer"
    />
  );
}

export default UpdateDonneesJudiciaires;
